import {
  useCallback,
  useEffect, useMemo, useState,
} from 'react';
import {
  Box, Grid, Paper,
  Radio, Stack,
} from '@mui/material';
import { Table } from 'react-bootstrap';
import axios from 'axios';
import BlockProps from '../../../models/BlockProps';
import BlockContactData from './BlockContactData';
import ModalComponent from '../../ModalComponent';
import Paginator from '../../Paginator';
import { PaginationMetaLink } from '../../../models/Paginator';
import config from '../../../config';
import IframeComponent from './IframeComponent';

function BlockContact({
  data, isEditing, isTemplate, editHandler,
}: BlockProps) {
  const props = (data.data as BlockContactData);
  const [iframeSrc, setIframeSrc] = useState(props?.html?.value ?? '');

  const [accessToken, setAccessToken] = useState('');
  const [failMessage, setFailMessage] = useState<string>(null);
  const [contactFormList, setContactFormList] = useState(null);
  const [isOpenListModal, setIsOpenListModal] = useState(false);

  const staticDefaultLinks = [
    { active: false, label: '&laquo; Previous', url: null } as PaginationMetaLink,
    { active: true, label: '1', url: null } as PaginationMetaLink,
    { active: false, label: 'Next &raquo;', url: null } as PaginationMetaLink,
  ];

  const [selectedRow, setSelectedRow] = useState(null);

  const [total, setTotal] = useState(0);
  const [fromItem, setFromItem] = useState(0);
  const [toItem, setToItem] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [links, setLinks] = useState<PaginationMetaLink[]>(staticDefaultLinks);

  const changePage = (page: number) => {
    // code to update pagination
  };

  const getContactList = useCallback(async (accessToken: any) => {
    if (!accessToken) {
      console.error('Access token is not set');
      return;
    }

    try {
      const requestUrl = `${config.api.webdbUrl}/simple_forms?form_type=1`;
      const response = await axios.get(requestUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.status !== 200 || response?.data?.original?.error) {
        setFailMessage('一覧を取得することができませんでした。');
      } else {
        const resultItems = response.data.data;
        setContactFormList(resultItems);

        // Adjust code to properly display pagination results when API is fixed
        setTotal(response?.data?.data?.length ?? 0);
        setToItem(response?.data?.data?.length ?? 0);
        setFromItem(response?.data?.data?.length ? 1 : 0);
      }
    } catch (error) {
      setFailMessage('一覧を取得することができませんでした。');
      console.error('Error:', error);
    }
  }, []);

  const getAccessToken = useCallback(async () => {
    try {
      const apiToken = localStorage.getItem(config.api.webdbKeyStorage);
      const requestUrl = `${config.api.webdbUrl}/tokens`;
      const response = await axios.post(requestUrl, {}, {
        headers: {
          Authorization: `x-api-key ${apiToken}`,
        },
      });

      if (response.status !== 200 || response?.data?.original?.error) {
        setFailMessage('一覧を取得することができませんでした。');
      } else {
        setAccessToken(response.data.token);
        getContactList(response.data.token);
      }
    } catch (error) {
      setFailMessage('一覧を取得することができませんでした。');
      console.error('Error:', error);
    }
    return '';
  }, [getContactList]);

  const selectRowFromCurrentValue = (url: string | null) => {
    if (!url || !contactFormList) {
      return;
    }

    const selected = contactFormList.find((item: any) => item.iframe_url === url);
    if (selected) {
      setSelectedRow(selected);
    }
  };

  const handleClick = () => {
    if (!isEditing || isTemplate) {
      return;
    }
    setFailMessage('');

    getAccessToken();

    setIsOpenListModal(true);
    selectRowFromCurrentValue(iframeSrc);
  };

  const handleSave = () => {
    if (props.html) {
      props.html.value = selectedRow.iframe_url;
      setIframeSrc(selectedRow.iframe_url);
    }

    editHandler(data.id, props);
    setIsOpenListModal(false);
  };

  const handleRadioChange = (item: any) => {
    setSelectedRow(item);
  };

  const trData = useMemo(() => {
    if (contactFormList && contactFormList?.length === 0) {
      return (
        <tr>
          <td colSpan={3} className="text-center">
            一覧がありません。
          </td>
        </tr>
      );
    }

    return (contactFormList && contactFormList?.length > 0) && (
      contactFormList?.map((item: any) => (
        <tr key={item?.id} onClick={() => handleRadioChange(item)}>
          <td className="text-center px-0" style={{ width: 100 }}>
            <Radio
              value={item?.id}
              checked={selectedRow && selectedRow.id === item.id}
              onChange={() => handleRadioChange(item)}
            />
            <span style={{ marginRight: 15 }}>選択</span>
          </td>
          <td className="text-center" style={{ width: 50 }}>{item?.id}</td>
          <td>
            {item?.name}
          </td>
        </tr>
      ))
    );
  }, [contactFormList, selectedRow]);

  useEffect(() => {
    if (isEditing && !isTemplate) {
      getAccessToken();
    }
  }, [getAccessToken, isEditing, isTemplate]);

  return (
    <>
      <Stack
        spacing={0}
        style={{
          overflow: 'hidden',
        }}
      >
        <Grid
          container
          direction="column"
          sx={{
            justifyContent: 'center', alignItems: 'center', p: 2,
          }}
          onClick={handleClick}
          className="block-content-wrapper"
        >
          {iframeSrc && (
            <div style={{
              pointerEvents: isEditing ? 'none' : 'auto',
              width: '100%',
            }}
            >
              <IframeComponent iframeSrc={iframeSrc} />
            </div>
          )}
          {!iframeSrc && (
            <Paper
              elevation={0}
              sx={{
                width: '100%', height: 450, background: '#F4F4F6', border: '1px dashed lightGray',
              }}
            >
              <div className="p-1">
                ***ここにお問い合わせが表示される***
              </div>
            </Paper>
          )}
        </Grid>
        {isEditing && !isTemplate && (
          <ModalComponent
            size="xl"
            title="お問合せ選択"
            confirmButtonText="選択する"
            onConfirm={handleSave}
            show={isOpenListModal}
            closeModal={() => setIsOpenListModal(false)}
            centered
            animation={false}
            scrollable
            backdropClassName="z-index-3000"
            style={{ zIndex: 3001 }}
          >
            {failMessage && (
              <Box className="text-danger pb-2">
                {failMessage}
              </Box>
            )}
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th className="text-center px-0 pr-1">選択</th>
                  <th className="text-center">No</th>
                  <th>名前</th>
                </tr>
              </thead>
              <tbody>
                {trData}
              </tbody>
            </Table>
            <div className="row mt-1">
              <div className="col-6">
                <div className="paging-info d-flex justify-content-start align-items-center mt-2">
                  <div>
                    {' '}
                    全
                    {' '}
                    {total}
                    {' '}
                    件中
                    {' '}
                    {fromItem}
                    {' '}
                    〜
                    {toItem}
                    {' '}
                    件を表示
                  </div>
                </div>
              </div>
              <div className="col-6 d-flex justify-content-end mt-2">
                <div>
                  <Paginator currentPage={currentPage} changePage={changePage} links={links} />
                </div>
              </div>
            </div>
          </ModalComponent>
        )}
      </Stack>
    </>
  );
}

export default BlockContact;
